// Sealed classified dossier — hover/tap to open, reveals the partnership brief entry point.
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Crown, Fingerprint, ArrowRight } from "lucide-react";

const COPY = {
  education: {
    caseNo: "CASE FILE BLV-EDU-2027", clearance: "EDUCATION NETWORK",
    line: "Somewhere inside your classrooms is the next founding creative.",
    cta: "Open the Education Brief", to: "/transmission/education",
  },
  industry: {
    caseNo: "CASE FILE BLV-IND-2027", clearance: "INDUSTRY NETWORK",
    line: "Your brand could be the next mission.",
    cta: "Open the Industry Brief", to: "/transmission/industry",
  },
};

const Redacted = ({ w }) => <span className={`inline-block h-2.5 ${w} bg-silver/25`} aria-hidden="true" />;

export const ClassifiedDossier = ({ kind = "industry" }) => {
  const c = COPY[kind] || COPY.industry;
  const reduced = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [canHover] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.("(hover: hover)")?.matches);

  return (
    <div
      className="relative mx-auto w-full max-w-md cursor-pointer select-none"
      onMouseEnter={() => canHover && setOpen(true)}
      onClick={() => (canHover ? setOpen(true) : setOpen((o) => !o))}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
      aria-expanded={open}
      data-testid={`classified-dossier-${kind}`}
    >
      <div className="gold-frame grain-dark relative overflow-hidden bg-charcoal p-7 sm:p-8">
        <div className="pointer-events-none absolute inset-2 border border-gold/15" />
        <AnimatePresence mode="wait">
          {!open ? (
            <motion.div key="closed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 0.3 }}>
              <div className="flex items-start justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/60 bg-onyx">
                  <Crown size={20} className="text-gold" strokeWidth={1.5} />
                </div>
                <Fingerprint size={26} className="text-gold/40" strokeWidth={1.2} />
              </div>
              <p className="mt-5 font-mono text-[9px] uppercase tracking-[0.35em] text-silver/50">{c.caseNo}</p>
              <div className="mt-3 inline-block -rotate-2 border border-gold px-3 py-1">
                <span className="font-display text-lg uppercase tracking-[0.15em] text-gold">Top Secret</span>
              </div>
              <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.25em] text-silver/60">Clearance: {c.clearance} · BLV eyes only</p>
              <div className="mt-4 space-y-2">
                <p className="space-x-2"><Redacted w="w-24" /> <Redacted w="w-40" /> <Redacted w="w-16" /></p>
                <p className="space-x-2"><Redacted w="w-32" /> <Redacted w="w-20" /> <Redacted w="w-28" /></p>
                <p className="space-x-2"><Redacted w="w-16" /> <Redacted w="w-36" /></p>
              </div>
              <div className="mt-5 flex items-end justify-between">
                <p className="font-marker text-sm text-goldlight">{`${canHover ? "hover" : "tap"} to unseal ✦`}</p>
                <div className="flex h-6 items-end gap-[2px]" aria-hidden="true">
                  {[3, 1, 2, 1, 3, 2, 1, 3, 1, 2, 3, 1, 2, 1, 3].map((h, i) => (
                    <span key={i} className="w-[2px] bg-silver/40" style={{ height: `${h * 7}px` }} />
                  ))}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ opacity: 0, y: reduced ? 0 : 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 0.35 }}>
              <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-gold">
                <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gold align-middle" aria-hidden="true" />
                File unsealed
              </p>
              <h3 className="mt-4 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-4xl">
                Classified<br />Partnership Brief
              </h3>
              <p className="mt-4 font-body text-sm leading-relaxed text-silver/85">{c.line}</p>
              <Link to={c.to} onClick={(e) => e.stopPropagation()} data-testid={`dossier-open-${kind}-btn`}
                className="btn-sweep mt-6 inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-6 py-3 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5">
                {c.cta} <ArrowRight size={14} />
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
